import ValidationSuccess from '~/composer/composer/lib/validation/ValidationSuccess';
import ValidationFail from '~/composer/composer/lib/validation/ValidationFail';
import {
  GBP_END_DATE_IN_PAST,
  GBP_INVALID_FORMAT_CTA_LINK,
  GBP_INVALID_FORMAT_REDEEM_LINK,
  GBP_INVALID_FORMAT_START_END_TIME,
  GBP_INVALID_HOUR,
  GBP_INVALID_MINUTES,
  GBP_MAX_CHAR_COUPON_CODE,
  GBP_MAX_CHAR_TERMS_CONDITIONS,
  GBP_MAX_CHAR_TITLE,
  GBP_MISSING_CTA_LINK,
  GBP_MISSING_EVENT_TITLE,
  GBP_MISSING_OFFER_TITLE,
  GBP_MISSING_START_END_DATE,
  GBP_MISSING_START_END_TIME,
  GBP_START_DATE_AFTER_END_DATE,
  GBP_START_DATE_IN_PAST,
  GBP_START_TIME_BEFORE_END_TIME,
} from '~/composer/composer/lib/validation/utils/validationErrorMessages';
import dayjs from 'dayjs';
import { transformStringTo24HrTime } from '~/composer/composer/utils/StringTimeTransformer';
import { isValidHttpUrl } from '~/composer/composer/lib/validation/utils/isValidHttpUrl';
import { isValidURL } from '~/grid/util';

const validateGBPTitle = (updateType, gbpData) => {
  // validation only for Event and Offer GBP posts
  if (updateType === 'whats_new') {
    return new ValidationSuccess();
  }

  if (!gbpData?.title) {
    return new ValidationFail(
      updateType === 'offer' ? GBP_MISSING_OFFER_TITLE : GBP_MISSING_EVENT_TITLE
    );
  }

  if (gbpData?.title.length > 58) {
    return new ValidationFail(GBP_MAX_CHAR_TITLE);
  }

  return new ValidationSuccess();
};

const validateGBPCouponCode = (updateType, gbpData) => {
  // validation only for Offer GBP posts
  if (updateType !== 'offer') {
    return new ValidationSuccess();
  }

  if (!gbpData?.code) {
    return new ValidationSuccess();
  }

  if (gbpData.code.length > 58) {
    return new ValidationFail(GBP_MAX_CHAR_COUPON_CODE);
  }

  return new ValidationSuccess();
};

const validateGBPDateTime = (updateType, gbpData) => {
  // validation only for Offer and Event GBP posts containing dates
  if (updateType === 'whats_new') {
    return new ValidationSuccess();
  }

  if (!gbpData?.start_date || !gbpData?.end_date) {
    return new ValidationFail(GBP_MISSING_START_END_DATE);
  }

  if (
    gbpData.start_date > gbpData.end_date ||
    (!gbpData?.start_time &&
      !gbpData?.end_time &&
      gbpData.start_date === gbpData.end_date)
  ) {
    return new ValidationFail(GBP_START_DATE_AFTER_END_DATE);
  }

  if (
    (gbpData?.start_time && !gbpData?.end_time) ||
    (!gbpData?.start_time && gbpData?.end_time)
  ) {
    return new ValidationFail(GBP_MISSING_START_END_TIME);
  }

  if (
    parseInt(gbpData.start_date.toString(), 10) + 86400 <
    dayjs().startOf('day').unix()
  ) {
    return new ValidationFail(GBP_START_DATE_IN_PAST);
  }

  if (!gbpData?.start_time && !gbpData?.end_time) {
    return new ValidationSuccess();
  }

  const pattern = new RegExp('^\\d?\\d:?\\d?\\d?\\s*([AaPp][mM])?$');
  if (
    gbpData.start_time &&
    gbpData.end_time &&
    (!pattern.test(gbpData.start_time.trim()) ||
      !pattern.test(gbpData.end_time.trim()))
  ) {
    return new ValidationFail(GBP_INVALID_FORMAT_START_END_TIME);
  }

  const startTime = transformStringTo24HrTime(gbpData.start_time as string);
  const endTime = transformStringTo24HrTime(gbpData.end_time as string);

  if (
    gbpData.start_date === gbpData.end_date &&
    startTime.hours > endTime.hours
  ) {
    return new ValidationFail(GBP_START_TIME_BEFORE_END_TIME);
  }

  if (
    gbpData.start_date === gbpData.end_date &&
    startTime.hours === endTime.hours &&
    startTime.minutes >= endTime.minutes
  ) {
    return new ValidationFail(GBP_START_TIME_BEFORE_END_TIME);
  }

  if (
    startTime.hours > 23 ||
    endTime.hours > 23 ||
    startTime.hours < 0 ||
    endTime.hours < 0
  ) {
    return new ValidationFail(GBP_INVALID_HOUR);
  }

  if (
    startTime.minutes > 59 ||
    endTime.minutes > 59 ||
    startTime.minutes < 0 ||
    endTime.minutes < 0
  ) {
    return new ValidationFail(GBP_INVALID_MINUTES);
  }

  if (
    parseInt(gbpData.end_date.toString(), 10) ===
      dayjs().utc().startOf('day').unix() &&
    dayjs().unix() >
      dayjs()
        .startOf('day')
        .add(endTime.hours, 'hour')
        .add(endTime.minutes, 'minute')
        .unix()
  ) {
    return new ValidationFail(GBP_END_DATE_IN_PAST);
  }

  return new ValidationSuccess();
};

const validateGBPCTA = (updateType, gbpData) => {
  // validation only for What's New and Event GBP posts
  if (updateType === 'offer') {
    return new ValidationSuccess();
  }

  if (gbpData?.cta && gbpData.cta !== 'none' && !gbpData.link) {
    return new ValidationFail(GBP_MISSING_CTA_LINK);
  }

  if (
    gbpData?.cta &&
    gbpData.cta !== 'none' &&
    !isValidHttpUrl(gbpData.link as string)
  ) {
    return new ValidationFail(GBP_INVALID_FORMAT_CTA_LINK);
  }

  return new ValidationSuccess();
};

const validateGBPRedeemLink = (updateType, gbpData) => {
  // validation only for Offer GBP posts
  if (updateType !== 'offer') {
    return new ValidationSuccess();
  }

  if (gbpData?.link && !isValidURL(gbpData.link)) {
    return new ValidationFail(GBP_INVALID_FORMAT_REDEEM_LINK);
  }

  return new ValidationSuccess();
};

const validateGBPTerms = (updateType, gbpData) => {
  // validation only for Offer GBP posts
  if (updateType !== 'offer') {
    return new ValidationSuccess();
  }

  if (gbpData?.terms && gbpData.terms.length > 5000) {
    return new ValidationFail(GBP_MAX_CHAR_TERMS_CONDITIONS);
  }

  return new ValidationSuccess();
};

const validateGBPChannelData = (updateType, gbpData) => {
  return [
    validateGBPCTA(updateType, gbpData),
    validateGBPTitle(updateType, gbpData),
    validateGBPDateTime(updateType, gbpData),
    validateGBPRedeemLink(updateType, gbpData),
    validateGBPTerms(updateType, gbpData),
    validateGBPCouponCode(updateType, gbpData),
  ].filter((result) => !result.isValid);
};

export const gbpChannelDataValidation = {
  validateGBPCTA,
  validateGBPCouponCode,
  validateGBPDateTime,
  validateGBPRedeemLink,
  validateGBPTerms,
  validateGBPTitle,
  validateGBPChannelData,
};
